import React, { ReactNode, useState } from 'react';

import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import styled, { keyframes } from 'styled-components';

const allowOverflow = keyframes`
  to { overflow: visible; }
`;

import { expandCard } from './helpers';

import { COLORS } from '../../../utils/theme/colors';
import { ListPagination } from '../ListPagination/ListPagination';

interface ICardListConstructor {
  childRef: any;
  paginationCallback?: (newList: any) => void;
  pureData?: any[];
  InnerComponent: React.FC<any>;
  data: {
    header: {
      text: string;
      sideIcon: any;
      action: () => void;
      size: string | '1%';
      dropdown: ReactNode;
    }[];
    cardsList: {
      value: string;
      subValue: string;
      icon: any;
      textColor: string;
      size: string | '1%';
    }[][];
    innerData: any[];
  };
  riskText?: any;
  checkboxAvailable?: any;
  selectMode?: any;
  checkedList?: any;
  setCheckedList?: any;
  pagination?: any;
}

const CardListConstructor: React.FC<ICardListConstructor> = ({
  data,
  InnerComponent,
  paginationCallback,
  pureData,
  childRef,
  riskText = false,
  checkboxAvailable = false,
  selectMode = false,
  checkedList = [],
  setCheckedList = [],
  pagination,
}) => {
  const { header, cardsList, innerData } = data;
  const [selected, setselected] = useState<number | null>(null);

  const cardsListLimited = cardsList?.slice(0, 10);

  const checkingItem = (card, e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setCheckedList((checkedList) => [...checkedList, card[0]?.value]);
    } else {
      setCheckedList(checkedList.filter((item) => item !== card[0]?.value));
    }
  };

  return (
    <Div>
      <div className='header_list_constructor'>
        {header?.map((item) => {
          return (
            <div
              key={item.text}
              className='header_item cursor'
              style={{ flexBasis: item.size }}
              onClick={item.action}
            >
              {item.text}
              <div className='header-icon-container'>{item.sideIcon}</div>
              {item.dropdown}
            </div>
          );
        })}
      </div>
      <div className='cards_list'>
        {cardsListLimited?.map((card, index) => {
          const [cardWithTextColor]: any = card.filter(
            (card: any) => card?.textColor,
          );

          return (
            <div
              style={{ display: 'flex', position: 'relative' }}
              key={'card' + index}
            >
              {checkboxAvailable && selectMode && (
                <Checkbox
                  className='checkbox'
                  checked={
                    checkedList.indexOf(card[0].value) > -1 ? true : false
                  }
                  disabled={cardWithTextColor.textColor !== '#fd5d5d'}
                  onChange={(e) => {
                    checkingItem(card, e);
                  }}
                />
              )}
              <div
                className='card_in_list cursor'
                onClick={(e) => {
                  setselected(index);
                  expandCard(e);
                }}
              >
                <div className='card_header'>
                  {card.map(
                    ({ icon, subValue, value, size, textColor }, index) => {
                      return (
                        <div
                          key={'value-' + index}
                          className='card_values'
                          style={{ flexBasis: size, maxWidth: size }}
                        >
                          {icon}
                          <div className='card_value_label'>
                            <p
                              className='main_card_text'
                              style={{
                                color: textColor,
                                textDecoration: riskText
                                  ? 'line-through'
                                  : undefined,
                              }}
                            >
                              {value}
                            </p>
                            <p className='sub_card_text'>{subValue}</p>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
                {selected === index ? (
                  <InnerComponent
                    {...{
                      className: 'card_inner_content',
                      index,
                      selected: selected === index,
                      data: innerData,
                    }}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
        <ListPagination
          ownRef={childRef}
          callback={paginationCallback}
          pureData={pureData}
          pagination={pagination}
        />
      </div>
    </Div>
  );
};

export default CardListConstructor;

const Div = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  color: #444;

  .header_list_constructor {
    position: sticky;
    top: 64px;
    left: 0;
    height: 50px;
    width: 100%;
    margin: 25px auto 16px;
    display: flex;
    flex-direction: row;
    font-weight: 400;
    font-size: 14px;
    color: #444;
    z-index: 4;
    pointer-events: none;
    background-color: #fff;
    border-radius: 3px;
  }

  .header_item {
    display: flex;
    align-items: center;
    padding-right: 5px;
    padding-left: 20px;
    height: 50px;
    user-select: none;
    pointer-events: all;
    transition: background-color 200ms ease-in-out;
    position: relative;
    font-weight: 700;

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }

    :hover .polygon path {
      fill: ${COLORS.primary};
    }

    .connector_header_item_clicked {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .card_in_list {
    width: 100%;
    height: 55px;
    margin-bottom: 16px;
    box-shadow: 0 0 4px 0px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
    transition: all ease-in-out 200ms;
    background-color: #fff;
    border: 1px solid #fff;
    overflow: hidden;

    :hover {
      background-color: #f0f0f0;
    }

    &.gabrr_expanded {
      position: sticky;
      top: 130px;
      left: 0;
      height: 580px;
      z-index: 3;
      animation: ${allowOverflow} 0s 200ms forwards;
    }

    &.gabrr_expanded .card_inner_content {
      height: calc(100% - 60px);
      width: 100%;
      z-index: 3;
    }
  }

  .card_header {
    display: flex;
    flex-direction: row;
    height: 55px;
    user-select: none;

    .card_values {
      padding-left: 20px;
      display: flex;
      align-items: center;
    }

    .card_value_label {
      width: 100%;

      .main_card_text {
        margin: 0;
        font-size: 14px;
        font-weight: 400;
        align-items: center;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .sub_card_text {
        margin: 0;
        font-size: 10px;
        font-weight: 400;
      }
    }
  }

  .checkbox {
    display: flex;
    position: absolute;
    top: 40%;
  }
`;
